import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useAlertDialogStore,
  type AlertDialogAction as StoreAction,
} from "./store";
import { Button } from "@/components/ui/button";

export function GlobalAlertDialog() {
  const {
    isOpen,
    title,
    description,
    actions,
    resolvePromise,
    hide,
    reset,
    mode,
    promptPlaceholder,
    promptDefaultValue,
  } = useAlertDialogStore();
  const [inputValue, setInputValue] = React.useState<string>(
    promptDefaultValue ?? "",
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // If the dialog is closed without an action (e.g., Esc or overlay click)
      if (resolvePromise) {
        // For `confirm` dialogs, a dismiss should be treated as 'false'
        // For generic `showAlert`, a dismiss is `null`
        // We can check if the actions match the default confirm actions
        const isConfirmDialog =
          actions.length === 2 &&
          actions.some((a) => a.label === "Okay" && a.value === true) &&
          actions.some((a) => a.label === "Cancel" && a.value === false);

        if (isConfirmDialog) {
          resolvePromise(false);
        } else {
          resolvePromise(null);
        }
      }
      reset(); // Reset the store state
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          {title && <AlertDialogTitle>{title}</AlertDialogTitle>}
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {mode === "prompt" && (
          <div className="px-2 pb-2">
            <input
              className="w-full h-9 rounded-md border bg-background px-2 text-sm"
              placeholder={promptPlaceholder}
              defaultValue={promptDefaultValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </div>
        )}
        <AlertDialogFooter>
          {actions.map((action, index) => {
            // The shadcn AlertDialogAction automatically closes the dialog.
            // AlertDialogCancel also closes the dialog.
            // We need to ensure our promise is resolved BEFORE the dialog closes.
            const originalOnClick = () => {
              if (resolvePromise) {
                if (mode === "prompt") {
                  // Return string on Okay; null on Cancel
                  const isOkay = action.value === true;
                  resolvePromise(isOkay ? inputValue : null);
                } else {
                  resolvePromise(action.value);
                }
              }
              hide();
            };

            if (
              action.variant === "outline" &&
              (action.label.toLowerCase() === "cancel" || actions.length === 1)
            ) {
              return (
                <AlertDialogCancel
                  key={index}
                  onClick={originalOnClick}
                  asChild
                >
                  <Button variant={action.variant || "outline"}>
                    {action.label}
                  </Button>
                </AlertDialogCancel>
              );
            }
            return (
              <AlertDialogAction key={index} onClick={originalOnClick} asChild>
                <Button variant={action.variant || "default"}>
                  {action.label}
                </Button>
              </AlertDialogAction>
            );
          })}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
